// frontend/src/services/reinsuranceMock.js

import { MOCK_ENABLED } from './mockConfig';
import { mockDashboardData } from '../mocks/reinsurance/dashboardData';
import { 
  mockContractsList, 
  mockContractDetails, 
  mockCreateContractResponse, 
  mockUpdateContractResponse,
  mockVerifyRNRResponse
} from '../mocks/reinsurance/contractsData';
import { 
  mockClaimsList, 
  mockClaimDetails, 
  mockRegisterClaimResponse, 
  mockProcessClaimResponse, 
  mockSettleClaimResponse, 
  mockClaimsReportResponse 
} from '../mocks/reinsurance/claimsData';
import { 
  mockCapitalPosition, 
  mockAllocateCapitalResponse, 
  mockUpdateAllocationResponse, 
  mockOptimizeAllocationResponse 
} from '../mocks/reinsurance/capitalData';
import { 
  mockExposureData, 
  mockModelExposureResponse, 
  mockAssessRiskResponse, 
  mockPriceRiskResponse 
} from '../mocks/reinsurance/riskData';
import {
  mockGenerateReportResponse,
  mockInvestorReportResponse,
  mockRegulatoryReportResponse
} from '../mocks/reinsurance/reportingData';

// Helper to simulate API delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

export const reinsuranceMockService = {
  // Dashboard data
  getDashboardData: async () => {
    await delay();
    return mockDashboardData;
  },

  // Contract Management
  listContracts: async (filters = {}) => {
    await delay();
    
    // Apply filters to mock data if needed
    let filteredData = [...mockContractsList.data];
    
    if (filters.status) {
      filteredData = filteredData.filter(contract => 
        contract.performance.current_status === filters.status
      );
    }
    
    if (filters.contractType) {
      filteredData = filteredData.filter(contract => 
        contract.contract_type === filters.contractType
      );
    }
    
    return {
      ...mockContractsList,
      count: filteredData.length,
      data: filteredData
    };
  },

  getContract: async (contractId) => {
    await delay();
    
    if (!mockContractDetails[contractId]) {
      return {
        success: false,
        message: `Contract not found: ${contractId}`
      };
    }
    
    return mockContractDetails[contractId];
  },

  createContract: async (contractData) => {
    await delay();
    return mockCreateContractResponse;
  },

  updateContract: async (contractId, contractData) => {
    await delay();
    
    if (!mockContractDetails[contractId]) {
      return {
        success: false,
        message: `Contract not found: ${contractId}`
      };
    }
    
    return {
      ...mockUpdateContractResponse,
      data: {
        ...mockUpdateContractResponse.data,
        contract_id: contractId
      }
    };
  },

  verifyRNR: async (rnr) => {
    await delay();
    return mockVerifyRNRResponse;
  },

  // Claim Management
  listClaims: async (filters = {}) => {
    await delay();
    
    // Apply filters to mock data if needed
    let filteredData = [...mockClaimsList.data];
    
    if (filters.contractId) {
      filteredData = filteredData.filter(claim => 
        claim.contract_id === filters.contractId
      );
    }
    
    if (filters.status) {
      filteredData = filteredData.filter(claim => 
        claim.status.current_status === filters.status
      );
    }
    
    return {
      ...mockClaimsList,
      count: filteredData.length,
      data: filteredData
    };
  },

  getClaim: async (claimId) => {
    await delay();
    
    if (!mockClaimDetails[claimId]) {
      return {
        success: false,
        message: `Claim not found: ${claimId}`
      };
    }
    
    return mockClaimDetails[claimId];
  },

  registerClaim: async (claimData) => {
    await delay();
    return mockRegisterClaimResponse;
  },

  processClaim: async (claimId, claimData) => {
    await delay();
    return mockProcessClaimResponse;
  },

  settleClaim: async (claimId, claimData) => {
    await delay();
    return mockSettleClaimResponse;
  },

  getClaimsReport: async (filters = {}) => {
    await delay();
    return mockClaimsReportResponse;
  },

  // Capital Management
  getCapitalPosition: async () => {
    await delay();
    return mockCapitalPosition;
  },

  allocateCapital: async (allocationData) => {
    await delay();
    return mockAllocateCapitalResponse;
  },

  updateAllocation: async (allocationId, allocationData) => {
    await delay();
    return mockUpdateAllocationResponse;
  },

  optimizeAllocation: async (capitalId, optimizationParams) => {
    await delay();
    return mockOptimizeAllocationResponse;
  },

  // Risk Modeling
  getExposureData: async () => {
    await delay();
    return mockExposureData;
  },

  modelExposure: async (contractId) => {
    await delay();
    return mockModelExposureResponse;
  },

  assessRisk: async (contractId, modelParams) => {
    await delay();
    return mockAssessRiskResponse;
  },

  priceRisk: async (contractId, pricingParams) => {
    await delay();
    return mockPriceRiskResponse;
  },

  // Reporting
  generateReport: async (reportType, reportParams) => {
    await delay();
    return mockGenerateReportResponse;
  },

  getInvestorReport: async (investorId, reportParams) => {
    await delay();
    return mockInvestorReportResponse;
  },

  getRegulatoryReport: async (regime, reportParams) => {
    await delay();
    return mockRegulatoryReportResponse;
  },

  // Cedent Management
  listCedents: (filters = {}) => {
    // Add more detailed logging to help debug
    console.log('MOCK: Listing cedents with filters:', filters, 'Called from:', new Error().stack.split('\n')[2]);
    
    // Import mock data
    const { mockCedentsList } = require('../mocks/reinsurance/cedentsData');
    
    let filteredCedents = mockCedentsList.data;
    
    // Apply filters
    if (filters.name) {
      const nameFilter = filters.name.toLowerCase();
      filteredCedents = filteredCedents.filter(cedent => 
        cedent.cedent_name.toLowerCase().includes(nameFilter)
      );
    }
    
    if (filters.domicile) {
      const domicileFilter = filters.domicile.toLowerCase();
      filteredCedents = filteredCedents.filter(cedent => 
        cedent.cedent_domicile.toLowerCase().includes(domicileFilter)
      );
    }
    
    return {
      success: true,
      count: filteredCedents.length,
      data: filteredCedents
    };
  },

  getCedent: (cedentId) => {
    console.log('MOCK: Getting cedent with ID:', cedentId);
    
    // Import mock data
    const { mockCedentDetails } = require('../mocks/reinsurance/cedentsData');
    
    if (!cedentId) {
      return {
        success: false,
        message: 'Cedent ID is required'
      };
    }
    
    const cedentDetails = mockCedentDetails[cedentId];
    
    if (!cedentDetails) {
      return {
        success: false,
        message: 'Cedent not found'
      };
    }
    
    return cedentDetails;
  },

  createCedent: (cedentData) => {
    console.log('MOCK: Creating cedent with data:', cedentData);
    
    // Import mock data
    const { mockCreateCedentResponse } = require('../mocks/reinsurance/cedentsData');
    
    // Generate a new ID
    const cedentId = `ced-${Date.now()}`;
    
    return {
      ...mockCreateCedentResponse,
      data: {
        ...mockCreateCedentResponse.data,
        cedent_id: cedentId,
        cedent_name: cedentData.cedent_name
      }
    };
  },

  updateCedent: (cedentId, cedentData) => {
    console.log('MOCK: Updating cedent with ID:', cedentId, 'and data:', cedentData);
    
    // Import mock data
    const { mockUpdateCedentResponse } = require('../mocks/reinsurance/cedentsData');
    
    if (!cedentId) {
      return {
        success: false,
        message: 'Cedent ID is required'
      };
    }
    
    return {
      ...mockUpdateCedentResponse,
      data: {
        ...mockUpdateCedentResponse.data,
        cedent_id: cedentId,
        cedent_name: cedentData.cedent_name
      }
    };
  },

  getCedentPerformance: (cedentId) => {
    console.log('MOCK: Getting performance for cedent with ID:', cedentId);
    
    if (!cedentId) {
      return {
        success: false,
        message: 'Cedent ID is required'
      };
    }
    
    // Return mock performance data
    return {
      success: true,
      data: {
        cedent_id: cedentId,
        total_contracts: 5,
        active_contracts: 3,
        total_premium: 7500000,
        total_limit: 45000000,
        loss_ratio: 42.5,
        claims: {
          total: 3,
          open: 1,
          closed: 2,
          total_incurred: 3187500,
          total_paid: 2125000
        },
        performance_by_year: [
          {
            year: 2023,
            contracts: 2,
            premium: 3000000,
            losses: 1275000,
            loss_ratio: 42.5
          },
          {
            year: 2024,
            contracts: 3,
            premium: 4500000,
            losses: 1912500,
            loss_ratio: 42.5
          }
        ]
      }
    };
  },

  // Helper methods - these are the same as in the real service
  formatCurrency: (amount, currency = 'USD', abbreviated = false) => {
    if (amount === undefined || amount === null) return '';
    
    if (abbreviated && amount >= 1000000) {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: currency,
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1
      }).format(amount);
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  },
  
  formatPercentage: (value) => {
    if (value === undefined || value === null) return '';
    
    return `${value}%`;
  },
  
  formatDate: (dateString) => {
    if (!dateString) return '';
    
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },
  
  isExpired: (dateString) => {
    if (!dateString) return false;
    
    const date = new Date(dateString);
    const now = new Date();
    
    return date < now;
  },
  
  getContractTypeOptions: () => {
    return [
      { value: 'property', label: 'Property' },
      { value: 'specialty', label: 'Specialty' },
      { value: 'ilw', label: 'ILW' },
      { value: 'parametric', label: 'Parametric' },
      { value: 'facultative', label: 'Facultative' }
    ];
  },
  
  getContractStructureOptions: () => {
    return [
      { value: 'proportional', label: 'Proportional' },
      { value: 'non_proportional', label: 'Non-Proportional' },
      { value: 'hybrid', label: 'Hybrid' }
    ];
  },
  
  getContractStatusOptions: () => {
    return [
      { value: 'draft', label: 'Draft' },
      { value: 'quoted', label: 'Quoted' },
      { value: 'bound', label: 'Bound' },
      { value: 'active', label: 'Active' },
      { value: 'expired', label: 'Expired' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'commuted', label: 'Commuted' }
    ];
  }
};